// Bootstrap de l'app et orchestration du parcours complet, dans l'ordre du README « Architecture » :
//
//   photo capturée -> lecture locale du code-barres (0 token si le produit est identifié)
//                  -> compression WebP 768 px (~80 Ko : le coût en tokens est proportionnel à la
//                     surface de l'image, et 80 Ko partent instantanément en 4G)
//                  -> POST /api/upload-url -> PUT direct vers le Blob -> POST /api/analyze (SSE)
//                  -> aliments affichés AU FIL DE L'EAU, poids en fourchette, curseurs locaux
//
// La cible est un téléphone tenu à une main : capture par `<input type="file" capture="environment">`
// (pas de `getUserMedia` — la capture native donne une meilleure photo et évite la gestion des
// permissions), gros boutons, aucune saisie clavier obligatoire. Le glisser-déposer n'est là que
// pour le bureau.
//
// Import de photos existantes (NutriScan 10) : un SECOND `<input type="file" multiple>`, SANS
// `capture`, ouvre la pellicule plutôt que l'appareil photo — `capture="environment"` du bouton
// « Prendre une photo » ci-dessus le forcerait sur mobile, et ce n'est justement pas ce qu'on veut
// ici. Traitement, progression et enregistrement dans le journal (`source: 'import'`) vivent dans
// ui/importView.ts, pas ici : `main.ts` ne fait que router le choix de fichiers vers cet écran.
import { analyserPhoto, envoyerCorrection, AnalyseError } from './analyze/analyzeClient.js';
import { televerserPhoto, UploadError } from './analyze/uploadClient.js';
import type { AnalyzeDone } from './analyze/types.js';
import { createNativeDetector } from './barcode/nativeDetector.js';
import { lookupOffProduct } from './barcode/openFoodFacts.js';
import { selectEan } from './barcode/selectEan.js';
import {
  compresserImage,
  dependancesNavigateur,
  formaterOctets,
  imageDataDepuisFichier,
  type PhotoCompressee,
} from './capture/compression.js';
import { enregistrerRepas } from './journal/journalClient.js';
import { renderLookupResult } from './ui/produitOff.js';
import { creerVueImport } from './ui/importView.js';
import { creerVueJournal } from './ui/journalView.js';
import { creerVueResultat, type CorrectionPortion } from './ui/resultatView.js';
import { startScan } from './ui/scanCamera.js';
import { creerObtenteurJetonTurnstile } from './analyze/turnstile.js';

// Mode public (CLAUDE.md § Auth mode public) : deux réglages injectés au build par Cloudflare Pages
// (azure-pipelines.yml), absents en dev/access — dans ce cas Turnstile n'est jamais sollicité et le
// journal reste visible, comportement identique à avant NutriScan 11.
const modeAuth = (import.meta.env.VITE_AUTH_MODE as string | undefined) === 'public' ? 'public' : 'access';
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

const app = document.querySelector<HTMLDivElement>('#app');
if (app) demarrer(app);

function demarrer(app: HTMLDivElement): void {
  // Balisage statique (aucune donnée d'origine modèle ou Open Food Facts ici) : le rendu des
  // aliments, lui, se fait en DOM dans ui/resultatView.ts.
  app.innerHTML = `
    <header class="entete">
      <h1>NutriScan</h1>
      <p class="lead">Photographie ton repas : les aliments et leurs macros arrivent en quelques
        secondes, et tu corriges ce qui est faux.</p>
      <nav class="entete-nav">
        <button type="button" class="lien-journal" id="bouton-voir-journal" ${modeAuth === 'public' ? 'hidden' : ''}>Voir mon journal</button>
      </nav>
    </header>

    <p id="hors-ligne" class="bandeau-hors-ligne" role="status" hidden>
      Hors connexion : la photo peut être prise, l'analyse partira dès le retour du réseau.
    </p>

    <section class="capture" id="zone-capture" aria-labelledby="titre-capture">
      <h2 id="titre-capture" class="visuellement-cache">Capture</h2>
      <p class="conseil">
        <strong>Laisse une référence d'échelle dans le cadre</strong> — une main, une fourchette ou
        une pièce à côté de l'assiette. C'est ce qui permet d'estimer la portion en grammes.
      </p>
      <div class="boutons-capture">
        <button type="button" class="primaire bouton-capture" id="bouton-photo">
          Prendre une photo du repas
        </button>
        <button type="button" class="bouton-capture" id="bouton-importer">
          Importer une photo
        </button>
      </div>
      <input type="file" id="photo-input" accept="image/*" capture="environment"
        class="visuellement-cache" />
      <input type="file" id="import-input" accept="image/*" multiple class="visuellement-cache" />
      <p class="depot-note">Sur ordinateur : dépose une image ici, ou colle-la (Ctrl+V).</p>
    </section>

    <p id="statut" class="statut" role="status" aria-live="polite"></p>

    <div id="erreur" class="erreur" role="alert" hidden>
      <p id="erreur-message"></p>
      <button type="button" class="primaire" id="bouton-reessayer">Réessayer</button>
    </div>

    <figure id="apercu" class="apercu" hidden>
      <img id="apercu-image" alt="Photo du repas analysée" />
      <figcaption id="apercu-legende"></figcaption>
    </figure>

    <div id="off-result"></div>

    <details class="autres-actions">
      <summary>Scanner un code-barres à la caméra</summary>
      <p class="conseil">Utile pour un produit emballé : la composition vient d'Open Food Facts,
        sans appeler de modèle et sans frais.</p>
      <button type="button" id="scan-button">Démarrer le scan</button>
      <video id="scan-video" playsinline muted hidden></video>
    </details>

    <div id="turnstile-conteneur" class="visuellement-cache" aria-hidden="true"></div>
  `;

  const boutonPhoto = app.querySelector<HTMLButtonElement>('#bouton-photo')!;
  const input = app.querySelector<HTMLInputElement>('#photo-input')!;
  const boutonImporter = app.querySelector<HTMLButtonElement>('#bouton-importer')!;
  const importInput = app.querySelector<HTMLInputElement>('#import-input')!;
  const zoneCapture = app.querySelector<HTMLElement>('#zone-capture')!;
  const statutEl = app.querySelector<HTMLParagraphElement>('#statut')!;
  const erreurEl = app.querySelector<HTMLDivElement>('#erreur')!;
  const erreurMessageEl = app.querySelector<HTMLParagraphElement>('#erreur-message')!;
  const boutonReessayer = app.querySelector<HTMLButtonElement>('#bouton-reessayer')!;
  const apercuEl = app.querySelector<HTMLElement>('#apercu')!;
  const apercuImage = app.querySelector<HTMLImageElement>('#apercu-image')!;
  const apercuLegende = app.querySelector<HTMLElement>('#apercu-legende')!;
  const offResultEl = app.querySelector<HTMLDivElement>('#off-result')!;
  const horsLigneEl = app.querySelector<HTMLParagraphElement>('#hors-ligne')!;
  const scanButton = app.querySelector<HTMLButtonElement>('#scan-button')!;
  const videoEl = app.querySelector<HTMLVideoElement>('#scan-video')!;
  const boutonVoirJournal = app.querySelector<HTMLButtonElement>('#bouton-voir-journal')!;
  const turnstileConteneur = app.querySelector<HTMLDivElement>('#turnstile-conteneur')!;

  // Turnstile invisible (CLAUDE.md § Auth mode public) : un seul obtenteur pour toute la session,
  // un jeton frais par appel à /api/analyze. `null` hors mode public (ou site key absente en
  // dev/access) : `analyser()` n'en demande alors jamais.
  const obtenirJetonTurnstile =
    modeAuth === 'public' && turnstileSiteKey
      ? creerObtenteurJetonTurnstile(turnstileSiteKey, turnstileConteneur)
      : null;

  // État du parcours en cours : ce qu'il faut pour reprendre après un échec sans redemander la photo.
  let photo: PhotoCompressee | null = null;
  let blobId: string | null = null;
  let analyseId: string | null = null;
  let correctionsEnAttente: CorrectionPortion[] = [];
  let apercuUrl: string | null = null;
  let enCours: AbortController | null = null;

  const vue = creerVueResultat({
    surCorrection: (correction) => {
      // `analyseId` n'arrive qu'avec l'évènement `done` : une correction faite pendant le streaming
      // attend la fin de l'analyse plutôt que d'être perdue.
      if (!analyseId) {
        correctionsEnAttente.push(correction);
        return;
      }
      void remonterCorrection(correction);
    },
    // Erreur laissée telle quelle (JournalError) : le bouton du bloc journal (resultatView.ts) la
    // rattrape lui-même pour afficher son propre message, pas la peine de la retraiter ici.
    // Absent en mode public (CLAUDE.md § Journal alimentaire, mode public) : `journal.ts` renvoie
    // 404 côté API — ce bouton ne doit même pas être proposé, plutôt que d'échouer au clic.
    surEnregistrerRepas:
      modeAuth === 'public'
        ? undefined
        : (repasAliments) =>
            enregistrerRepas({ aliments: repasAliments, source: 'modele', analyseId: analyseId ?? undefined }).then(
              () => undefined,
            ),
  });
  offResultEl.before(vue.element);

  // Écran « Journal » (CLAUDE.md § Journal alimentaire) : un second écran, affiché à la demande,
  // qui remplace la capture le temps de le consulter — pas de routeur, une seule page.
  // Désactivé en mode public (CLAUDE.md § Journal alimentaire, mode public — NutriScan 11) : une IP
  // n'est ni stable ni unique, la mélanger dans le journal mélangerait les repas de visiteurs
  // distincts. Le bouton est déjà masqué (markup ci-dessus) ; on évite en plus de monter l'écran.
  const vueJournal = modeAuth === 'public' ? null : creerVueJournal();
  if (vueJournal) app.append(vueJournal.element);
  let journalVisible = false;
  function basculerJournal(): void {
    if (!vueJournal) return;
    journalVisible = !journalVisible;
    zoneCapture.hidden = journalVisible;
    vue.element.hidden = journalVisible || vue.nombreAliments === 0;
    offResultEl.hidden = journalVisible;
    vueJournal.element.hidden = !journalVisible;
    boutonVoirJournal.textContent = journalVisible ? 'Revenir à la capture' : 'Voir mon journal';
    if (journalVisible) void vueJournal.actualiser();
  }
  boutonVoirJournal.addEventListener('click', basculerJournal);

  // Écran « Importer des photos » (NutriScan 10) : même principe que le journal ci-dessus, un
  // second écran qui remplace la capture le temps de l'import. Déclenché uniquement par le choix de
  // fichiers (`import-input`), jamais par un simple clic — pas la peine d'un écran vide.
  const vueImport = creerVueImport({
    onFermer() {
      zoneCapture.hidden = false;
      vue.element.hidden = vue.nombreAliments === 0;
      offResultEl.hidden = false;
    },
  });
  app.append(vueImport.element);
  boutonImporter.addEventListener('click', () => importInput.click());
  importInput.addEventListener('change', () => {
    if (importInput.files && importInput.files.length > 0) {
      zoneCapture.hidden = true;
      vue.element.hidden = true;
      offResultEl.hidden = true;
      vueImport.ouvrirAvec(importInput.files);
    }
    // Remis à zéro comme #photo-input : reprendre DEUX FOIS le même lot doit redéclencher `change`.
    importInput.value = '';
  });

  function remonterCorrection(correction: CorrectionPortion): Promise<boolean> {
    if (!analyseId) return Promise.resolve(false);
    // Échec silencieux assumé : une correction non remontée n'a aucune conséquence pour
    // l'utilisateur, elle ne manque qu'au jeu d'évaluation.
    return envoyerCorrection({ analyseId, blobId: blobId ?? undefined, ...correction }).catch(() => false);
  }

  function statut(message: string): void {
    statutEl.textContent = message;
  }

  // `reprise` absent = refus qu'il ne sert à rien de rejouer tout de suite (plafond de débit ou de
  // budget, api/src/rateLimit.ts et api/src/budgetGuard.ts) : le bouton disparaît plutôt que de
  // promettre un « Réessayer » qui répéterait indéfiniment le même refus.
  function afficherErreur(message: string, reprise?: () => void): void {
    erreurMessageEl.textContent = message;
    erreurEl.hidden = false;
    boutonReessayer.hidden = !reprise;
    if (reprise) {
      boutonReessayer.onclick = () => {
        erreurEl.hidden = true;
        reprise();
      };
      boutonReessayer.focus();
    } else {
      boutonReessayer.onclick = null;
    }
  }

  function majHorsLigne(): void {
    horsLigneEl.hidden = navigator.onLine;
  }
  window.addEventListener('online', majHorsLigne);
  window.addEventListener('offline', majHorsLigne);
  majHorsLigne();

  boutonPhoto.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const fichier = input.files?.[0];
    if (fichier) void traiterFichier(fichier);
    // Remis à zéro pour que reprendre DEUX FOIS la même photo redéclenche bien un `change`.
    input.value = '';
  });

  // Glisser-déposer et collage : bureau uniquement, la capture reste le chemin principal.
  for (const type of ['dragenter', 'dragover'] as const) {
    zoneCapture.addEventListener(type, (event) => {
      event.preventDefault();
      zoneCapture.classList.add('survol');
    });
  }
  for (const type of ['dragleave', 'drop'] as const) {
    zoneCapture.addEventListener(type, () => zoneCapture.classList.remove('survol'));
  }
  zoneCapture.addEventListener('drop', (event) => {
    event.preventDefault();
    const fichier = event.dataTransfer?.files?.[0];
    if (fichier?.type.startsWith('image/')) void traiterFichier(fichier);
  });
  window.addEventListener('paste', (event) => {
    const fichier = Array.from(event.clipboardData?.files ?? []).find((f) => f.type.startsWith('image/'));
    if (fichier) void traiterFichier(fichier);
  });

  scanButton.addEventListener('click', () => {
    void startScan({
      scanButton,
      statusEl: statutEl,
      videoEl,
      surEan: async (ean) => {
        await chercherProduit(ean);
      },
    });
  });

  async function chercherProduit(ean: string): Promise<boolean> {
    offResultEl.textContent = 'Recherche sur Open Food Facts…';
    try {
      const lookup = await lookupOffProduct(ean);
      renderLookupResult(ean, lookup, offResultEl);
      return lookup.status === 'found';
    } catch {
      // Panne réseau ou 5xx d'OFF : jamais confondu avec « EAN inconnu » (barcode/openFoodFacts.ts).
      offResultEl.textContent = "Open Food Facts est injoignable pour l'instant.";
      return false;
    }
  }

  async function traiterFichier(fichier: File | Blob): Promise<void> {
    enCours?.abort();
    const controleur = new AbortController();
    enCours = controleur;

    erreurEl.hidden = true;
    offResultEl.replaceChildren();
    vue.reinitialiser();
    vue.element.hidden = true;
    photo = null;
    blobId = null;
    analyseId = null;
    correctionsEnAttente = [];

    statut('Lecture de la photo…');

    // 1. Code-barres d'abord : un produit emballé identifié, c'est la composition exacte et zéro
    //    token (README « Partis pris »). Un échec ici n'est jamais bloquant.
    //    Uniquement avec le détecteur NATIF : le repli ZXing pèse ~950 Ko de WASM, soit dix
    //    secondes en 4G avant même de commencer à analyser une assiette qui, neuf fois sur dix,
    //    n'a pas de code-barres. Sur un navigateur sans API native, viser un code-barres reste
    //    possible par le scan caméra explicite (ui/scanCamera.ts), qui charge ZXing en connaissance
    //    de cause.
    try {
      const detecteurNatif = await createNativeDetector();
      const imageData = detecteurNatif ? await imageDataDepuisFichier(fichier) : null;
      const ean = imageData && detecteurNatif ? selectEan(await detecteurNatif(imageData)) : null;
      if (ean) {
        statut(`Code-barres détecté : ${ean}`);
        if (await chercherProduit(ean)) {
          statut('Composition exacte trouvée — aucun appel au modèle.');
          return;
        }
      }
    } catch {
      // Lecteur indisponible ou image illisible par le décodeur : on laisse le modèle décider.
    }
    if (controleur.signal.aborted) return;

    // 2. Compression, AVANT tout envoi.
    try {
      statut('Compression de la photo…');
      photo = await compresserImage(fichier, dependancesNavigateur());
    } catch {
      statut('');
      afficherErreur(
        "Cette image n'a pas pu être lue. Reprends la photo, ou choisis un autre fichier.",
        () => boutonPhoto.click(),
      );
      return;
    }
    if (controleur.signal.aborted) return;

    afficherApercu(photo);
    await envoyerEtAnalyser(controleur);
  }

  function afficherApercu(compressee: PhotoCompressee): void {
    if (apercuUrl) URL.revokeObjectURL(apercuUrl);
    apercuUrl = URL.createObjectURL(compressee.blob);
    apercuImage.src = apercuUrl;
    apercuLegende.textContent =
      `Photo envoyée : ${formaterOctets(compressee.octets)} · ` +
      `${compressee.largeur}×${compressee.hauteur} px · ${compressee.type.replace('image/', '')}`;
    apercuEl.hidden = false;
  }

  async function envoyerEtAnalyser(controleur: AbortController): Promise<void> {
    const aEnvoyer = photo;
    if (!aEnvoyer) return;

    if (!navigator.onLine) {
      statut('');
      afficherErreur(
        "Pas de connexion : la photo est prête, l'analyse partira dès que le réseau revient.",
        () => void envoyerEtAnalyser(controleur),
      );
      return;
    }

    if (!blobId) {
      try {
        statut(`Envoi de la photo (${formaterOctets(aEnvoyer.octets)})…`);
        blobId = await televerserPhoto(aEnvoyer.blob, { signal: controleur.signal });
      } catch (err) {
        if (controleur.signal.aborted) return;
        statut('');
        afficherErreur(
          err instanceof UploadError ? err.message : "L'envoi de la photo a échoué.",
          () => void envoyerEtAnalyser(controleur),
        );
        return;
      }
    }
    if (controleur.signal.aborted) return;

    await analyser(controleur);
  }

  async function analyser(controleur: AbortController): Promise<void> {
    const idBlob = blobId;
    if (!idBlob) return;

    // Mode public : un jeton Turnstile FRAIS avant chaque appel (README « Turnstile invisible sur
    // le premier appel de la session » — en pratique un par appel, un widget rendu une seule fois).
    // La vérification qui compte est côté API (api/src/turnstile.ts) ; ici on ne fait qu'obtenir le
    // jeton à transmettre.
    let turnstileToken: string | undefined;
    if (obtenirJetonTurnstile) {
      statut('Vérification anti-robot…');
      try {
        turnstileToken = await obtenirJetonTurnstile();
      } catch (err) {
        if (controleur.signal.aborted) return;
        statut('');
        afficherErreur(
          err instanceof Error ? err.message : 'Vérification anti-robot indisponible.',
          () => void analyser(controleur),
        );
        return;
      }
      if (controleur.signal.aborted) return;
    }

    vue.reinitialiser();
    vue.element.hidden = false;
    statut('Analyse en cours — les aliments s’affichent au fur et à mesure…');

    let done: AnalyzeDone;
    try {
      done = await analyserPhoto(
        idBlob,
        {
          surAliment: (aliment) => {
            vue.ajouterAlimentDetecte(aliment);
            statut(`Analyse en cours — ${vue.nombreAliments} aliment(s) détecté(s)…`);
          },
          surReprise: () => {
            // Sortie hors schéma rejouée côté serveur : tout ce qui est affiché est caduc.
            vue.reinitialiser();
            statut('Reprise de l’analyse…');
          },
        },
        { signal: controleur.signal, turnstileToken },
      );
    } catch (err) {
      if (controleur.signal.aborted) return;
      statut('');
      if (err instanceof AnalyseError && err.code === 'photo_absente') {
        // Le blob n'existe plus côté serveur : rejouer l'analyse ne servirait à rien, il faut
        // re-téléverser la photo compressée (qu'on a toujours en mémoire).
        blobId = null;
        afficherErreur(err.message, () => void envoyerEtAnalyser(controleur));
        return;
      }
      if (err instanceof AnalyseError && (err.code === 'rate_limited' || err.code === 'budget_exceeded')) {
        // Refus qui ne dépend pas de cette photo (plafond de débit ou de budget) : proposer une
        // reprise immédiate rejouerait indéfiniment le même refus (périmètre NutriScan 7, § garde-fou
        // budgétaire — « signaler clairement dans l'interface »). Pas de bouton de reprise.
        afficherErreur(err.message);
        return;
      }
      const message =
        err instanceof AnalyseError
          ? err.message
          : !navigator.onLine
            ? 'Connexion perdue pendant l’analyse.'
            : "L'analyse s'est interrompue. Réessaie dans un instant.";
      // Sinon la photo est déjà dans le Blob : la reprise relance l'analyse seule, sans rien
      // re-téléverser ni redemander à l'utilisateur.
      afficherErreur(message, () => void analyser(controleur));
      return;
    }

    analyseId = done.analyseId;
    vue.terminer(done);
    statut(
      `Analyse terminée · ${done.modele} · ajuste les curseurs si une portion est fausse.`,
    );

    const enAttente = correctionsEnAttente;
    correctionsEnAttente = [];
    for (const correction of enAttente) void remonterCorrection(correction);
  }
}
